import React, { useMemo, useState } from "react";
import { Badge, IconButton, Stack, Box, Typography, CircularProgress, Button } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useTranslation } from "react-i18next";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import { GoMakeMenu } from "@/components";
import { CustomTooltip } from "@/widgets/header/components/custom-tooltip";
import { PrimaryButton } from "@/components/button/primary-button";
import { FONT_FAMILY } from "@/utils/font-family";
import { NotificationTabs, TabPanel } from "@/widgets/frontdesk-notifications/components/notification-tabs";
import { MessageNotificationItem } from "@/widgets/frontdesk-notifications/components/message-notification-item";
import { TicketNotificationItem } from "@/widgets/frontdesk-notifications/components/ticket-notification-item";
import { AiProcessedNotificationItem } from "@/widgets/frontdesk-notifications/components/ai-processed-notification-item";
import { InfiniteScrollObserver } from "@/widgets/frontdesk-notifications/components/infinite-scroll-observer";
import { useFrontdeskNotifications } from "@/widgets/frontdesk-notifications/hooks/use-frontdesk-notifications";
import { Permissions } from "@/components/CheckPermission/enum";
import { useUserPermission } from "@/hooks/use-permission";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { useTheme } from "@/contexts/theme/useTheme";
import { useSetRecoilState } from "recoil";
import { snackbarState } from "@/store/snackbar";
import {
  mapNotificationToTab,
  type BackendNotification,
} from "@/services/frontdesk/utils/notification-type-mapper";

interface IFrontDeskNavProps {
  /** Smaller trigger button, used by the mobile header. */
  compact?: boolean;
}

const FrontDeskNav = ({ compact }: IFrontDeskNavProps = {}) => {
  const { t } = useTranslation();
  const dir: "rtl" | "ltr" = t("direction");
  const { primaryColor, neutralColor } = useGomakeTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);
  const open = Boolean(anchorEl);
  const { CheckPermission } = useUserPermission();
  const setSnackbar = useSetRecoilState(snackbarState);
  const {
    notifications,
    aiProcessedItems,
    aiProcessedCount,
    counts,
    loading,
    error,
    hasMore,
    isLoadingMore,
    handleNotificationClick,
    handleAiProcessedClick,
    navigateToFrontdesk,
    retryFetch,
    loadMore,
    fetchNotifications,
    fetchAiProcessed,
    markAllAsRead,
    markAllAiProcessedAsRead,
  } = useFrontdeskNotifications();

  const { getIconOverride, currentTheme } = useTheme();

  // Get theme-aware Help icon (HP Client icon when theme is active)
  const FrontDeskIcon = getIconOverride("FrontDeskIcon");
  const isHpClient = currentTheme === "hp-client";

  // Make background color theme-aware: transparent for HP theme, default gray for others
  const iconButtonBackground = isHpClient ? "transparent" : "#f6f6f6";
  const hasUnread = counts.total > 0;

  const sortedNotifications = useMemo(() => {
    return [...notifications].sort((a, b) => {
      const aTime = new Date(a.createdAt).getTime() || 0;
      const bTime = new Date(b.createdAt).getTime() || 0;
      return bTime - aTime;
    });
  }, [notifications]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    // Menu open refresh must not trigger the toast — only background poll does
    void fetchNotifications(1, false, true, { source: "menu" });
    void fetchAiProcessed();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleNotificationItemClick = (notification: BackendNotification) => {
    handleNotificationClick(notification);
    handleClose();
  };

  const handleAiItemClick = (
    item: Parameters<typeof handleAiProcessedClick>[0]
  ) => {
    handleAiProcessedClick(item);
    handleClose();
  };

  const handleOpenFrontdesk = () => {
    navigateToFrontdesk();
    handleClose();
  };

  const handleRetry = () => {
    retryFetch();
  };

  const handleMarkAllAsRead = async () => {
    if (isMarkingAllRead) return;

    if (activeTab === 1) {
      markAllAiProcessedAsRead();
      setSnackbar({
        state: true,
        message: t("frontdesk.notifications.allMarkedAsRead") || "All notifications marked as read",
        type: "success",
      });
      return;
    }

    setIsMarkingAllRead(true);
    try {
      await markAllAsRead();
      setSnackbar({
        state: true,
        message: t("frontdesk.notifications.allMarkedAsRead") || "All notifications marked as read",
        type: "success",
      });
    } catch (error) {
      console.error("[FrontDeskNav] Mark all as read error:", error);
      setSnackbar({
        state: true,
        message: t("frontdesk.notifications.markAllFailed") || "Failed to mark notifications as read",
        type: "error",
      });
    } finally {
      setIsMarkingAllRead(false);
    }
  };

  // Match SystemNotifications / HP header icon size so the envelope
  // sits on the same baseline as bell / help.
  const iconSize = compact ? 18 : 22;

  const renderIconButton = () => (
    <CustomTooltip title={t("frontdesk.title")} arrow>
      <IconButton
        data-gm-action="mail"
        style={{
          backgroundColor: iconButtonBackground,
          padding: compact ? "4px" : "8px",
        }}
        onClick={handleClick}
        aria-label={t("frontdesk.title")}
      >
        {FrontDeskIcon ? (
          <FrontDeskIcon
            width={iconSize}
            height={iconSize}
            showBadge={isHpClient && hasUnread}
          />
        ) : (
          <EmailOutlinedIcon
            style={{
              color: "var(--theme-primary-color, #2E3092)",
              width: iconSize,
              height: iconSize,
            }}
          />
        )}
      </IconButton>
    </CustomTooltip>
  );

  const renderLoadingState = () => (
    <Box
      sx={{
        padding: "60px 20px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
      }}
    >
      <CircularProgress size={40} sx={{ color: primaryColor(500) }} />
      <Typography sx={{ ...FONT_FAMILY.Lexend(400, 14), color: neutralColor(600) }}>{t("frontdesk.notifications.loading")}</Typography>
    </Box>
  );

  const renderErrorState = () => (
    <Box
      sx={{
        padding: "40px 20px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        textAlign: "center",
      }}
    >
      <ErrorOutlineIcon sx={{ fontSize: 48, color: "#EF4444" }} />
      <Typography sx={{ ...FONT_FAMILY.Lexend(600, 16), color: neutralColor(900) }}>{t("frontdesk.notifications.failedToLoad")}</Typography>
      <Typography sx={{ ...FONT_FAMILY.Lexend(400, 13), color: neutralColor(600) }}>
        {error || t("frontdesk.notifications.errorOccurred")}
      </Typography>
      <Button
        onClick={handleRetry}
        startIcon={<RefreshIcon />}
        sx={{
          ...FONT_FAMILY.Lexend(600, 14),
          color: primaryColor(500),
          textTransform: "none",
          padding: "8px 16px",
          "&:hover": {
            backgroundColor: `${primaryColor(500)}10`,
          },
        }}
      >
        {t("frontdesk.retry")}
      </Button>
    </Box>
  );

  const renderEmptyState = (type: "messages" | "ai") => (
    <Box
      sx={{
        padding: "40px 20px",
        textAlign: "center",
        color: neutralColor(400),
      }}
    >
      <Typography sx={{ ...FONT_FAMILY.Lexend(400, 14) }}>
        {type === "messages"
          ? t("frontdesk.notifications.noMessageNotifications")
          : t("frontdesk.notifications.noAiProcessed", "No AI-processed messages yet")}
      </Typography>
    </Box>
  );

  const renderMessagesTab = () => {
    if (loading && sortedNotifications.length === 0) return renderLoadingState();
    if (error && sortedNotifications.length === 0) return renderErrorState();
    if (sortedNotifications.length === 0) return renderEmptyState("messages");

    return (
      <Stack>
        {sortedNotifications.map((notification) =>
          mapNotificationToTab(notification.type, notification) === "TICKET" ? (
            <TicketNotificationItem
              key={notification.id}
              notification={notification}
              onClick={handleNotificationItemClick}
            />
          ) : (
            <MessageNotificationItem
              key={notification.id}
              notification={notification}
              onClick={handleNotificationItemClick}
            />
          )
        )}
        <InfiniteScrollObserver
          onLoadMore={loadMore}
          hasMore={hasMore}
          isLoading={isLoadingMore}
        />
      </Stack>
    );
  };

  const renderAiTab = () => {
    if (aiProcessedItems.length === 0) return renderEmptyState("ai");

    return (
      <Stack>
        {aiProcessedItems.map((item) => (
          <AiProcessedNotificationItem
            key={`ai-${item.id}`}
            item={item}
            onClick={handleAiItemClick}
          />
        ))}
      </Stack>
    );
  };

  const renderMenu = () => (
    <GoMakeMenu
      handleClose={handleClose}
      open={open}
      anchorEl={anchorEl}
      noListPadding
      // Override HP contextual-menu Paper styles (alignItems/padding) so this
      // panel stretches full width and mirrors default-theme LTR/RTL layout.
      PaperProps={{
        sx: {
          width: 420,
          maxWidth: "calc(100vw - 32px)",
          maxHeight: 600,
          padding: 0,
          minWidth: 420,
          alignItems: "stretch",
          direction: dir,
          "& .MuiList-root": {
            padding: 0,
            width: "100%",
            alignItems: "stretch",
          },
        },
      }}
      sx={{
        "& .MuiPaper-root": {
          width: 420,
          maxWidth: "calc(100vw - 32px)",
          maxHeight: 600,
          padding: "0 !important",
          alignItems: "stretch !important",
        },
      }}
    >
      <Stack width="100%" height="100%" sx={{ direction: dir }}>
        {/* Header */}
        <Box
          sx={{
            padding: "16px 20px",
            borderBottom: `1px solid ${neutralColor(200)}`,
            direction: dir,
            textAlign: "start",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "4px",
              direction: dir,
              gap: 1,
            }}
          >
            <Typography
              sx={{
                ...FONT_FAMILY.Lexend(600, 16),
                color: primaryColor(900),
                textAlign: "start",
              }}
            >
              {t("frontdesk.notifications.title")}
            </Typography>
            {(activeTab === 1 ? aiProcessedItems.length > 0 : counts.total > 0) && (
              <Typography
                component="span"
                onClick={handleMarkAllAsRead}
                sx={{
                  ...FONT_FAMILY.Lexend(500, 13),
                  color: primaryColor(500),
                  cursor: isMarkingAllRead ? "not-allowed" : "pointer",
                  opacity: isMarkingAllRead ? 0.6 : 1,
                  textDecoration: "none",
                  flexShrink: 0,
                  "&:hover": {
                    textDecoration: isMarkingAllRead ? "none" : "underline",
                  },
                }}
              >
                {isMarkingAllRead
                  ? t("frontdesk.notifications.markingAll") || "Marking..."
                  : t("frontdesk.notifications.markAllAsRead") || "Mark all as read"}
              </Typography>
            )}
          </Box>
          <Typography
            sx={{
              ...FONT_FAMILY.Lexend(400, 12),
              color: neutralColor(600),
              textAlign: "start",
            }}
          >
            {t("frontdesk.notifications.unreadCount", {
              count: counts.total,
            })}
          </Typography>
        </Box>

        <NotificationTabs
          activeTab={activeTab}
          onTabChange={handleTabChange}
          messageBadgeCount={counts.total}
          aiBadgeCount={aiProcessedCount}
        />

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            maxHeight: 400,
            width: "100%",
          }}
        >
          <TabPanel value={activeTab} index={0}>
            {renderMessagesTab()}
          </TabPanel>
          <TabPanel value={activeTab} index={1}>
            {renderAiTab()}
          </TabPanel>
        </Box>

        {/* Footer Button */}
        <Box
          sx={{
            padding: "12px 20px",
            borderTop: `1px solid ${neutralColor(200)}`,
          }}
        >
          <PrimaryButton
            onClick={handleOpenFrontdesk}
            style={{
              width: "100%",
              backgroundColor: primaryColor(500),
              color: "#ffffff",
              padding: "10px 16px",
              fontSize: 14,
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "6px",
            }}
          >
            {t("frontdesk.openFrontDesk")}
          </PrimaryButton>
        </Box>
      </Stack>
    </GoMakeMenu>
  );

  // Always keep Badge wrapper so mark-all-as-read does not remount the trigger
  // (which would invalidate Menu anchorEl and jump the panel across the page).
  // HP draws the red dot inside the SVG (same as the bell); hide the MUI badge there.
  return !CheckPermission(Permissions.SHOW_FRONT_DESK) ? (
    <div></div>
  ) : (
    <Stack
      direction="row"
      alignItems="center"
      spacing={compact ? 0.25 : 0.5}
      sx={{ lineHeight: 0 }}
    >
      <Badge
        variant="dot"
        color="error"
        invisible={isHpClient || !hasUnread}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          verticalAlign: "middle",
          padding: isHpClient ? 0 : "2px",
          lineHeight: 0,
        }}
        componentsProps={{
          badge: {
            style: {
              right: "2px",
              top: "2px",
            },
          },
        }}
      >
        {renderIconButton()}
        {renderMenu()}
      </Badge>
    </Stack>
  );
};

export { FrontDeskNav };
